import React, { useEffect, useState } from 'react';
import {
  DownOutlined,
  SearchOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { Form, Icon as LegacyIcon } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import {
  Empty,
  Table,
  Tag,
  Input,
  Button,
  Descriptions,
  Drawer,
  Modal,
  Menu,
  Dropdown,
  message,
  Popconfirm,
} from 'antd';
import { FormComponentProps } from '@ant-design/compatible/lib/form';
import firebase from 'firebase';
import Highlighter from 'react-highlight-words';
import { any } from 'prop-types';
import CreateDriverDrawer from './createDriverDrawer';
import { FETCH_DRIVER_SERVICES, FETCH_ALL_DRIVERS_LIST } from './queries';
import uuid from 'uuid/v4';
import { CREATE_DRIVER } from './mutations';
import UpdateDriverDrawer from './updateDriverDrawer';
import { useQuery } from '@apollo/client';
import { getMobileApk } from '../../utils/mobile';
import { sendEmail } from '../../utils/email';
import { getClient } from '../../apollo';
import {
  Driver,
  useDeleteDriverMutation,
  useGiveDriverAccessToAppMutation,
} from '../../graphql/datamodel.gen';
import { getTableScroll } from '../../utils/table';

const createDriver = async (
  email: string,
  password: string,
  volcanoDriverId: string,
) => {
  // @ts-ignore
  const response = await getClient().mutate({
    mutation: CREATE_DRIVER,
    variables: {
      email,
      password,
      volcanoDriverId,
    },
  });

  return response.data;
};

const fetchDriverServices = async (id: string) => {
  // @ts-ignore
  const response = await getClient().query({
    query: FETCH_DRIVER_SERVICES,
    variables: {
      id,
    },
    fetchPolicy: 'network-only',
  });

  return response.data.driver.services.reverse() || [];
};

export default function DriversContainer() {
  const [state, setState] = useState({
    drivers: [],
  });
  const response = useQuery(FETCH_ALL_DRIVERS_LIST, {
    pollInterval: 10000,
    notifyOnNetworkStatusChange: true,
  });
  const [deleteDriverMutation] = useDeleteDriverMutation({
    client: getClient(),
  });

  const [giveDriverAccessToAppMut] = useGiveDriverAccessToAppMutation({
    client: getClient(),
  });

  const deleteDriver = async (id: string) => {
    try {
      const response = await deleteDriverMutation({
        variables: {
          id,
        },
      });

      return response.data.deleteDriver.deleted;
    } catch (e) {
      return false;
    }
  };

  const giveDriverAccessToApp = (
    id: string,
    email: string,
    password: string,
  ) => {
    return giveDriverAccessToAppMut({
      variables: {
        id,
        email,
        password,
      },
    });
  };

  const { data, refetch, networkStatus } = response;

  useEffect(() => {
    if (data && data.drivers) {
      setState({
        drivers: data.drivers,
      });
    }
  }, [data]);

  const loading = networkStatus === 1 || networkStatus === 2;

  return (
    <DriversView
      drivers={state.drivers}
      loading={loading}
      refetch={refetch}
      onDeleteDriver={deleteDriver}
      giveDriverAccessToApp={giveDriverAccessToApp}
    />
  );
}

interface IFormComponentProps extends FormComponentProps {
  extraProps: {
    onSubmit: any;
    record: any;
  };
  giveDriverAccessToApp: (
    id: string,
    email: string,
    password: string,
  ) => Promise<any>;
}

interface IFormComponentState {
  loading: boolean;
}

class DriverAccountCreationForm extends React.Component<
  IFormComponentProps,
  IFormComponentState
> {
  state = {
    loading: false,
  };
  validatePassword = (
    rule: any,
    value: any,
    callback: { (arg0: string): void; (): void },
  ) => {
    // @ts-ignore
    const { form } = this.props;
    if (form.getFieldValue('password').length < 6) {
      callback(
        'Por favor, utiliza una contraseña que contenga como mínimo 6 caracteres',
      );
    } else {
      callback();
    }
  };

  validateEmail = async (
    rule: any,
    value: any,
    callback: (arg0?: string) => void,
  ) => {
    // @ts-ignore
    const { form } = this.props;

    const valid = /.+@.+\..+/.test(form.getFieldValue('email'));

    if (!valid) {
      callback('Por favor, utiliza un correo electrónico válido');
    } else {
      const signInMethods = await firebase
        .auth()
        .fetchSignInMethodsForEmail(form.getFieldValue('email'));

      const userExists = signInMethods.length > 0 ? true : false;

      if (userExists) {
        callback('Ya existe un conductor con ese email');
      } else {
        callback();
      }
    }
  };

  handleSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    this.props.form.validateFields(async (err, values) => {
      if (!err) {
        const { email, password } = values;
        const { id } = this.props.extraProps.record;
        try {
          this.setState({
            loading: true,
          });
          message.loading({
            content: 'Dando acceso..',
            key: 'give_access',
          });
          await this.props.giveDriverAccessToApp(id, email, password);
          message.success({
            content: 'Acceso condecido',
            key: 'give_access',
          });
          this.props.extraProps.onSubmit(true);
        } catch (error) {
          message.error({
            content: 'Error al dar acceso al conductor',
            key: 'give_access',
          });
          console.error('Error creating driver', error);
          this.props.extraProps.onSubmit(false);
        } finally {
          this.setState({
            loading: false,
          });
          setTimeout(() => {
            message.destroy();
          }, 2000);
        }
      }
    });
  };

  render() {
    // @ts-ignore
    const { getFieldDecorator } = this.props.form;

    return (
      <Form
        id={'driver-account-creation-form'}
        onSubmit={this.handleSubmit}
        layout="vertical"
        hideRequiredMark
      >
        <Form.Item label="Correo electrónico">
          {getFieldDecorator('email', {
            rules: [
              {
                required: true,
                message: 'Por favor, introduce un correo electrónico',
              },
              {
                validator: this.validateEmail,
              },
            ],
          })(<Input placeholder={'ejemplo@email.com'} />)}
        </Form.Item>

        <Form.Item label="Contraseña">
          {getFieldDecorator('password', {
            rules: [
              {
                required: true,
                message:
                  'Por favor, introduce una contraseña son 6 carácteres o más',
              },
              {
                validator: this.validatePassword,
              },
            ],
          })(<Input placeholder="Contraseña" />)}
        </Form.Item>
      </Form>
    );
  }
}

const MyDriverAccountCreationForm = Form.create<IFormComponentProps>()(
  DriverAccountCreationForm,
);

class DriverAccountCreationModal extends React.Component<
  {
    visible: boolean;
    record: any;
    hideDrawer: () => void;
    giveDriverAccessToApp: (
      id: string,
      email: string,
      password: string,
    ) => Promise<any>;
  },
  { creating: boolean }
> {
  state = {
    creating: false,
  };

  onSubmit = (success: boolean) => {
    this.setState({
      creating: false,
    });
    this.props.hideDrawer();
  };

  render() {
    return (
      <div>
        <Modal
          centered={true}
          footer={[
            <Button onClick={() => this.props.hideDrawer()}>Cerrar</Button>,
            <Button
              onClick={() => {
                this.setState({
                  creating: true,
                });
              }}
              loading={this.state.creating}
              form="driver-account-creation-form"
              key="submit"
              htmlType="submit"
              type={'primary'}
            >
              Dar acceso
            </Button>,
          ]}
          title="Dar acceso a aplicación móvil"
          width={300}
          destroyOnClose={true}
          onCancel={this.props.hideDrawer}
          maskClosable={true}
          closable={true}
          visible={this.props.visible}
        >
          {
            // @ts-ignore
            // @ts-nocheck
            <MyDriverAccountCreationForm
              extraProps={{
                record: this.props.record,
                onSubmit: this.onSubmit,
              }}
              giveDriverAccessToApp={this.props.giveDriverAccessToApp}
            />
          }
        </Modal>
      </div>
    );
  }
}

class DriversView extends React.Component<
  {
    drivers: any[];
    loading: boolean;
    refetch: (...args: any[]) => any;
    onDeleteDriver: (id: string) => Promise<boolean>;
    giveDriverAccessToApp: (
      id: string,
      email: string,
      password: string,
    ) => Promise<any>;
  },
  {
    filteredInfo: any;
    sortedInfo: any;
    searchText?: string;
    searchedColumn: string;
    giveAccessToAppModalVisible: boolean;
    modalVisible: boolean;
    createDriverModalVisible: boolean;
    updateDriverModalVisible: boolean;
    activeRecord?: any;
    driverServices: any[];
    actionsDropdownVisible: boolean;
  }
> {
  state = {
    filteredInfo: {},
    sortedInfo: {},
    searchText: undefined,
    searchedColumn: '',
    giveAccessToAppModalVisible: false,
    modalVisible: false,
    createDriverModalVisible: false,
    updateDriverModalVisible: false,
    driverServices: [],
    activeRecord: undefined,
    actionsDropdownVisible: false,
  };
  searchInput: any;

  handleChange = (pagination: any, filters: any, sorter: any) => {
    this.setState({
      filteredInfo: filters,
      sortedInfo: sorter,
    });
  };

  handleSearch = (
    selectedKeys: any[],
    confirm: () => void,
    dataIndex: string,
  ) => {
    confirm();
    this.setState({ searchText: selectedKeys[0], searchedColumn: dataIndex });
  };

  handleReset = (clearFilters: () => void, confirm: () => void) => {
    clearFilters();
    confirm();
    this.setState({ searchText: '' });
  };

  showCreateDriverDrawer = () => {
    this.setState({
      createDriverModalVisible: true,
    });
  };

  hideCreateDriverDrawer = async (result?: boolean) => {
    this.setState({
      createDriverModalVisible: false,
    });
    await this.props.refetch();
  };

  showDrawer = (record: any) => {
    this.setState({
      activeRecord: record,
      giveAccessToAppModalVisible: true,
    });
  };

  hideDrawer = async () => {
    this.setState({
      giveAccessToAppModalVisible: false,
      activeRecord: undefined,
    });

    await this.props.refetch();
  };

  showUpdateDriverDrawer = (record: any) => {
    this.setState({
      activeRecord: record,
      updateDriverModalVisible: true,
    });
  };

  hideUpdateDriverDrawer = async () => {
    this.setState({
      updateDriverModalVisible: false,
      activeRecord: undefined,
    });
    await this.props.refetch();
  };

  showModal = async (id: string) => {
    try {
      message.loading({
        content: 'Cargando lista de servicios',
        key: 'driver_services',
        duration: 0,
      });
      let services = await fetchDriverServices(id);
      message.success({
        content: 'Hecho!',
        key: 'driver_services',
        duration: 2,
      });
      services = services.map((service: any) => {
        if (service.departure) {
          const returnVal = {
            ...service,
            departureDate: service.departure.date,
          };
          delete returnVal.departure;

          return returnVal;
        }

        return service;
      });

      this.setState({
        modalVisible: true,
        driverServices: services,
      });
    } catch (e) {
    } finally {
      setTimeout(() => {
        message.destroy();
      }, 3000);
    }
  };

  handleModalOk = (e: any) => {
    this.setState({
      modalVisible: false,
    });
  };

  handleModalCancel = (e: any) => {
    this.setState({
      modalVisible: false,
    });
  };

  deleteDriverAccount = async (id: string) => {
    try {
      message.loading({
        content: 'Dando de baja..',
        key: 'delete_driver',
      });
      const result = await this.props.onDeleteDriver(id);
      if (result) {
        message.success({
          content: 'Hecho!',
          key: 'delete_driver',
        });
      } else {
        message.error({
          content: 'Ha habido un problema',
          key: 'delete_driver',
        });
      }
    } finally {
      setTimeout(() => {
        message.destroy();
      }, 2000);
    }
  };

  private clearAllFilters = () => {
    this.setState({
      filteredInfo: {},
      sortedInfo: {},
      searchText: '',
    });
  };

  render() {
    let { sortedInfo, filteredInfo } = this.state;
    sortedInfo = sortedInfo || {};
    filteredInfo = filteredInfo || {};

    const columns = [
      {
        title: 'ID',
        key: 'id',
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
        }: any): React.ReactNode => (
          <div style={{ padding: 8 }}>
            <Input
              ref={(node) => {
                this.searchInput = node;
              }}
              placeholder={`Buscar por ID`}
              value={selectedKeys[0]}
              onChange={(e) =>
                setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={() =>
                this.handleSearch(selectedKeys, confirm, 'id')
              }
              style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <Button
              type="primary"
              onClick={() => this.handleSearch(selectedKeys, confirm, 'id')}
              icon={<SearchOutlined translate={undefined} />}
              size="small"
              style={{ width: 90, marginRight: 8 }}
            >
              Buscar
            </Button>
            <Button
              onClick={() => this.handleReset(clearFilters, confirm)}
              size="small"
              style={{ width: 90 }}
            >
              Limpiar
            </Button>
          </div>
        ),
        filterIcon: (filtered: any): React.ReactNode => (
          <SearchOutlined
            style={{ color: filtered ? '#1890ff' : undefined }}
            translate={undefined}
          />
        ),
        onFilter: (value: string, record: any) => {
          const id = record.id || record.volcanoDriverId;
          return (id || '')
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase());
        },
        onFilterDropdownVisibleChange: (visible: any) => {
          if (visible) {
            setTimeout(() => this.searchInput.select());
          }
        },
        render: (text: string, record: any) => {
          const id = record.volcanoDriverId || '';
          return (
            <Highlighter
              highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
              searchWords={[this.state.searchText]}
              autoEscape
              textToHighlight={id.toString()}
            />
          );
        },
      },
      {
        title: 'Nombre',
        dataIndex: 'name',
        key: 'name',
        sorter: (a: any, b: any) => {
          if (
            a.name &&
            b.name &&
            typeof a.name === 'string' &&
            typeof b.name === 'string'
          ) {
            return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
          }

          return 0;
        },
        sortOrder:
          (sortedInfo as any).columnKey === 'name' && (sortedInfo as any).order,
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
        }: any): React.ReactNode => (
          <div style={{ padding: 8 }}>
            <Input
              ref={(node) => {
                this.searchInput = node;
              }}
              placeholder={`Buscar conductor`}
              value={selectedKeys[0]}
              onChange={(e) =>
                setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={() =>
                this.handleSearch(selectedKeys, confirm, 'name')
              }
              style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <Button
              type="primary"
              onClick={() => this.handleSearch(selectedKeys, confirm, 'name')}
              icon={<SearchOutlined translate={undefined} />}
              size="small"
              style={{ width: 90, marginRight: 8 }}
            >
              Buscar
            </Button>
            <Button
              onClick={() => this.handleReset(clearFilters, confirm)}
              size="small"
              style={{ width: 90 }}
            >
              Limpiar
            </Button>
          </div>
        ),
        filterIcon: (filtered: any): React.ReactNode => (
          <SearchOutlined
            style={{ color: filtered ? '#1890ff' : undefined }}
            translate={undefined}
          />
        ),
        onFilter: (
          value: string,
          record: {
            name: string;
          },
        ) =>
          (record['name'] || '')
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase()),
        onFilterDropdownVisibleChange: (visible: any) => {
          if (visible) {
            setTimeout(() => this.searchInput.select());
          }
        },
        render: (text: string) => {
          if (text && typeof text === 'string') {
            return (
              <Highlighter
                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                searchWords={[this.state.searchText]}
                autoEscape
                textToHighlight={text.toString()}
              />
            );
          }
        },
      },
      {
        title: 'Tipo',
        dataIndex: 'type',
        key: 'type',
        filters: [
          { text: 'Colaborador', value: 'collaborator' },
          { text: 'Propio', value: 'own' },
        ],
        // filteredValue: (filteredInfo as any).type || null,
        onFilter: (filter: string, record: any) => {
          return record.type === filter;
        },
        sorter: (a: any, b: any) => {
          if (
            a.type &&
            b.type &&
            typeof a.type === 'string' &&
            typeof b.type === 'string'
          ) {
            return a.type.length - b.type.length;
          }

          return 0;
        },
        sortOrder:
          (sortedInfo as any).columnKey === 'type' && (sortedInfo as any).order,
        render: (type: string) => {
          if (type === 'collaborator') return 'Colaborador';
          if (type === 'own') return 'Propio';
        },
      },
      // {
      //   title: 'Servicios',
      //   dataIndex: 'services',
      //   key: 'services',
      //   render: (text: any, record: any) => (
      //     <Button
      //       onClick={() => this.showModal(record.id || record.volcanoDriverId)}
      //       size={'small'}
      //     >
      //       Ver lista
      //     </Button>
      //   ),
      // },
      {
        title: 'Alta en app móvil',
        dataIndex: 'hasLoginCredentials',
        key: 'hasLoginCredentials',
        render: (hasLoginCredentials: boolean) => (
          <span>
            {(() => {
              let color;
              let localizedStatus = 'No';
              if (hasLoginCredentials) {
                color = 'green';
                localizedStatus = 'Sí';
              }
              if (!hasLoginCredentials) {
                color = 'volcano';
                localizedStatus = 'No';
              }
              return <Tag color={color}>{localizedStatus.toUpperCase()}</Tag>;
            })()}
          </span>
        ),
        filters: [
          { text: 'Sí', value: 'Sí' },
          { text: 'No', value: 'No' },
        ],
        // filteredValue: (filteredInfo as any).hasLoginCredentials || null,
        onFilter: (filter: string, record: any) => {
          if (filter === 'Sí') {
            return record.hasLoginCredentials === true;
          }

          if (filter === 'No') {
            return record.hasLoginCredentials === false;
          }

          return true;
        },
        defaultSortOrder: 'ascend' as 'ascend' | 'descend' | undefined,
        sorter: (a: any, b: any) => {
          const firstVal = a.hasLoginCredentials ? 0 : 1;
          const secondVal = b.hasLoginCredentials ? 0 : 1;

          return firstVal - secondVal;
        },
        sortOrder:
          (sortedInfo as any).columnKey === 'hasLoginCredentials'
            ? (sortedInfo as any).order
            : 'ascend',
      },
      // {
      //   title: 'Habilitado en el sistema',
      //   dataIndex: 'inPrisma',
      //   key: 'inPrisma',
      //   render: (inPrisma: boolean) => (
      //     <span>
      //       {(() => {
      //         let color;
      //         let localizedStatus = 'No';
      //         if (inPrisma) {
      //           color = 'green';
      //           localizedStatus = 'Sí';
      //         }
      //         if (!inPrisma) {
      //           color = 'volcano';
      //           localizedStatus = 'No';
      //         }
      //         return <Tag color={color}>{localizedStatus.toUpperCase()}</Tag>;
      //       })()}
      //     </span>
      //   ),
      //   filters: [
      //     { text: 'Sí', value: 'Sí' },
      //     { text: 'No', value: 'No' },
      //   ],
      //   filteredValue: (filteredInfo as any).inPrisma || null,
      //   onFilter: (filter: string, record: any) => {
      //     if (filter === 'Sí') {
      //       return record.inPrisma === true;
      //     }
      //
      //     if (filter === 'No') {
      //       return record.inPrisma === false;
      //     }
      //
      //     return true;
      //   },
      //   sorter: (a: any, b: any) => {
      //     const firstVal = a.inPrisma ? 0 : 1;
      //     const secondVal = b.inPrisma ? 0 : 1;
      //
      //     return firstVal - secondVal;
      //   },
      //   sortOrder:
      //     (sortedInfo as any).columnKey === 'inPrisma' &&
      //     (sortedInfo as any).order,
      // },
      {
        title: 'Estado',
        dataIndex: 'status',
        key: 'status',
        render: (status: string) => (
          <span>
            {(() => {
              let color;
              let localizedStatus = 'Inactivo';
              if (status === 'active') {
                color = 'green';
                localizedStatus = 'Activo';
              }
              if (status === 'inactive') {
                color = 'volcano';
                localizedStatus = 'Inactivo';
              }
              return (
                <Tag color={color} key={status}>
                  {localizedStatus.toUpperCase()}
                </Tag>
              );
            })()}
          </span>
        ),
        filters: [
          { text: 'Activo', value: 'active' },
          { text: 'Inactivo', value: 'inactive' },
        ],
        // filteredValue: (filteredInfo as any).status || null,
        onFilter: (filter: string, record: any) => {
          return filter === record.status;
        },
        sorter: (a: any, b: any) => {
          if (
            a.status &&
            b.status &&
            typeof a.status === 'string' &&
            typeof b.status === 'string'
          ) {
            return a.status.length - b.status.length;
          }
          return 0;
        },
        sortOrder:
          (sortedInfo as any).columnKey === 'status' &&
          (sortedInfo as any).order,
      },
      {
        title: 'Acciones',
        key: 'actions',
        render: (text: any, record: Driver) => {
          const sendAppLink = (
            <Menu.Item
              onClick={async () => {
                const url = await getMobileApk('driver');
                const email = record.volcanoEmail || record.email;
                sendEmail(
                  `mailto:${email}?subject=Aplicación móvil&body=${encodeURIComponent(
                    url as string,
                  )}`,
                );
              }}
            >
              Enviar aplicación móvil
            </Menu.Item>
          );

          if (
            record.inPrisma &&
            record.hasLoginCredentials &&
            record.inVolcano
          ) {
            const actionsMenu = (
              <Menu>
                {sendAppLink}
                <Menu.Divider />
                <Menu.Item style={{ color: '#f5222d' }} key="delete_account">
                  <Popconfirm
                    placement={'top'}
                    title="Seguro que quieres dar de baja a este conductor?"
                    onConfirm={() => this.deleteDriverAccount(record.id)}
                    okText="Si"
                    cancelText="No"
                  >
                    Dar de baja del sistema
                  </Popconfirm>
                </Menu.Item>
              </Menu>
            );

            return (
              <Dropdown
                overlay={actionsMenu}
                // onVisibleChange={visible => {
                //   this.setState({
                //     actionsDropdownVisible: visible,
                //   });
                // }}
              >
                <Button size="small">
                  Acciones <DownOutlined translate={undefined} />
                </Button>
              </Dropdown>
            );
          }
          if (record.inPrisma && !record.inVolcano) {
            const actionsMenu = (
              <Menu>
                {sendAppLink}
                <Menu.Divider />
                <Menu.Item
                  style={{ color: '#1890ff' }}
                  onClick={() => this.showUpdateDriverDrawer(record)}
                  key="update_driver"
                >
                  Gestionar conductor colaborador
                </Menu.Item>
              </Menu>
            );

            return (
              <Dropdown overlay={actionsMenu}>
                <Button size="small">
                  Acciones <DownOutlined translate={undefined} />
                </Button>
              </Dropdown>
            );
          }
          if (!record.hasLoginCredentials) {
            const actionsMenu = (
              <Menu>
                {sendAppLink}
                <Menu.Divider />
                <Menu.Item
                  style={{ color: '#52c41a' }}
                  onClick={() => this.showDrawer(record)}
                  key="create_mobile_account"
                >
                  Dar acceso a aplicación móvil
                </Menu.Item>
              </Menu>
            );
            return (
              <Dropdown overlay={actionsMenu}>
                <Button size="small">
                  Acciones <DownOutlined translate={undefined} />
                </Button>
              </Dropdown>
            );
          }
        },
      },
    ];

    const servicesColumns = [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
      },
      {
        title: 'Tipo',
        dataIndex: 'type',
        key: 'type',
      },
      {
        title: 'Fecha de salida',
        dataIndex: 'departureDate',
        key: 'departureDate',
      },
      {
        title: 'Estado',
        dataIndex: 'status',
        key: 'status',
        render: (status: string) => (
          <span>
            {(() => {
              let color;
              let localizedStatus = 'confirmado';
              if (status === 'assigned') {
                color = 'green';
                localizedStatus = 'asignado';
              }
              if (status === 'confirmed') {
                color = 'volcano';
                localizedStatus = 'confirmado';
              }
              return (
                <Tag color={color} key={status}>
                  {localizedStatus.toUpperCase()}
                </Tag>
              );
            })()}
          </span>
        ),
      },
    ];

    return (
      <div>
        <div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              paddingBottom: '2em',
            }}
          >
            <Button
              icon={<LegacyIcon type={'close'} />}
              style={{ marginRight: '10px' }}
              onClick={this.clearAllFilters}
            >
              Eliminar filtros
            </Button>
            <Button
              icon={<UserAddOutlined translate={undefined} />}
              type="primary"
              onClick={this.showCreateDriverDrawer}
            >
              Crear nuevo conductor colaborador
            </Button>
          </div>
        </div>

        <Table
          onRow={(record, index) => {
            return {
              onClick: (e) => {
                e.preventDefault();

                if (record.inPrisma && !record.inVolcano) {
                  return this.showUpdateDriverDrawer(record);
                }
                if (!record.hasLoginCredentials) {
                  return this.showDrawer(record);
                }
              },
            };
          }}
          loading={this.props.loading}
          locale={{
            emptyText: (
              <Empty
                description="No hay datos disponibles"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
          size={'middle'}
          pagination={{ pageSize: 25 }}
          rowKey={() => {
            return uuid();
          }}
          columns={columns}
          expandedRowRender={(record) => {
            return (
              <Descriptions size="small">
                <Descriptions.Item label="Teléfono">
                  {(record as any).alternatePhone || (record as any).phone}
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                  {(record as any).volcanoEmail || (record as any).email}
                </Descriptions.Item>
              </Descriptions>
            );
          }}
          dataSource={this.props.drivers}
          onChange={this.handleChange}
          // scroll={{ y: getTableScroll() }}
        />

        <DriverAccountCreationModal
          visible={this.state.giveAccessToAppModalVisible}
          hideDrawer={this.hideDrawer}
          record={this.state.activeRecord}
          giveDriverAccessToApp={this.props.giveDriverAccessToApp}
        />

        <CreateDriverDrawer
          onSubmit={this.hideCreateDriverDrawer}
          onClose={this.hideCreateDriverDrawer}
          visible={this.state.createDriverModalVisible}
        />

        {this.state.activeRecord && (
          <UpdateDriverDrawer
            hideDrawer={this.hideUpdateDriverDrawer}
            driverId={
              this.state.activeRecord
                ? (this.state.activeRecord as any).id
                : undefined
            }
            visible={this.state.updateDriverModalVisible}
            onClose={this.hideUpdateDriverDrawer}
            onSubmit={(success?: boolean) => this.hideUpdateDriverDrawer()}
          />
        )}

        <Modal
          title="Lista de servicios"
          visible={this.state.modalVisible}
          onOk={this.handleModalOk}
          onCancel={this.handleModalCancel}
          width={700}
        >
          <Table
            columns={servicesColumns}
            locale={{
              emptyText: (
                <Empty
                  description="No hay datos disponibles"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ),
            }}
            size={'middle'}
            dataSource={this.state.driverServices}
          />
          {/* <Table>
            size={'small'}
            pagination={{ pageSize: 10 }}
            columns={servicesColumns}
            dataSource=
            {[
              {
                type: '1',
              },
            ]}
          </Table> */}
        </Modal>
      </div>
    );
  }
}
